import { Alert, Box, Grid, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { ConfirmationDialogResult, DatabaseRecordType, RiskAssesmentFrequency, RiskType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import * as storeActions from "../../../models/store/storeActions";
import * as riskService from "../../../models/services/riskService";
import { ISessionState } from "../../../models/session/sessionState";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { DialogTabs } from "../../framework/Dialog/DialogTabs";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IdTitleNode, IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import { recordLockManager } from "source/models/services/recordLockService";
import { StoreUtils } from "source/models/store/storeUtils";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { archiveConfirmationWord, removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { Base } from "ui/Scripts/source/framework/base";
import { IRiskEditItem } from "ui/Scripts/source/models/risk/riskEditItem";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { ModifyUserInfo } from "source/components/framework/User/ModifyUserInfo";
import { PublishButton } from "source/components/framework/Button/PublishButton";
import { SaveDataObj } from "source/framework/saveData";
import { ISaveSuccess, SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { FormText } from "source/components/framework/FormText";
import { DatePicker } from "source/components/framework/DateTime/DatePicker";
import { RiskListStateChip } from "../RiskList/RiskListStateChip";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { RiskEditDialogAssessmentList } from "../RiskEditDialogAssessmentList";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { AttachmentListOfRecord } from "source/components/attachments/AttachmentList/AttachmentListOfRecord";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { FormCheckBox } from "source/components/framework/FormCheckBox";

/* eslint-disable no-unused-vars */
const enum RiskEditDialogTab {
    General = "1",
    Assessments = "2",
    Attachments = "3",
}
/* eslint-enable no-unused-vars */

interface IRiskEditDialogProp {
    sessionState: ISessionState;
    companyId: string;
    riskId: string;
    riskType: RiskType;
    riskTemplateId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onSaved: (id: string) => void;
    onClose: () => void;
    onPrint: () => void;
    onRefreshList: () => Promise<void>;
    onPrevious: () => void;
    onNext: () => void;
}

interface IRiskEditDialogState {
    activeTab: RiskEditDialogTab;
    isLoading: boolean;
    isNew: boolean;
    risk: IRiskEditItem;
    id: string;
    rowId: string;
    unitId: string;
    riskCategoryId: string;
    riskType: RiskType;
    dateStr: string;
    code: string;
    title: string;
    description: string;
    threat: string;
    controls: string;
    owner: string;
    assesmentFrequency: RiskAssesmentFrequency;
    opportunity: boolean;
    opportunityBenefit: string;
    opportunityDescription: string;
    canEdit: boolean;
    canRemove: boolean;
    units: IIdTitleNode[];
    riskCategories: IIdTitleNode[];
    riskProcesses: IIdTitleNode[];
    riskStandards: IIdTitleNode[];
    riskStakeholders: IIdTitleNode[];
    riskRiskProcessIds: string[];
    riskRiskStandardIds: string[];
    riskRiskStakeholderIds: string[];
    riskProcessName: string;
    lockedUser: IModifyUser;
    isLockedToOtherUser: boolean;
}

export const RiskEditDialog = (props: IRiskEditDialogProp) => {
    const _isMounted = useRef(false);
    const orgStateHash = useRef("");
    const theme = useTheme();

    const [state, setState] = useState<IRiskEditDialogState>({
        activeTab: RiskEditDialogTab.General,
        isLoading: false,
        isNew: false,
        risk: null,
        id: "",
        rowId: "",
        unitId: "",
        riskCategoryId: "",
        riskType: props.riskType,
        dateStr: "",
        code: "",
        title: "",
        description: "",
        threat: "",
        controls: "",
        owner: "",
        assesmentFrequency: null,
        opportunity: false,
        opportunityBenefit: "",
        opportunityDescription: "",
        canEdit: false,
        canRemove: false,
        units: [],
        riskCategories: [],
        riskProcesses: [],
        riskStandards: [],
        riskStakeholders: [],
        riskRiskProcessIds: [],
        riskRiskStandardIds: [],
        riskRiskStakeholderIds: [],
        riskProcessName: "",
        lockedUser: null,
        isLockedToOtherUser: false,
    });

    const loadRisk = async (id: string) => {
        const isNew = id === Base.emptyGuid && !state.risk;
        //Unlock current risk
        const unlockRecord = state.canEdit;
        if (unlockRecord && state.id && LockedRecord.isLockedToUser(state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(props.companyId, state.id, DatabaseRecordType.Risk);
        }
        //Load and lock risk
        setState((old) => ({ ...old, isLoading: true }));
        let canEdit = false;
        let canRemove = false;
        const result = await AppUtils.callService(() => riskService.getRiskEdit(props.companyId, id, props.riskTemplateId, true), false, false, () => setState((old) => ({ ...old, isLoading: false })));
        if (!result) return;
        const risk = result.item;
        canEdit = result.canEdit;
        canRemove = result.canRemove;
        const newState = {
            ...state,
            isLoading: false,
            isNew,
            risk,
            id: risk.id,
            rowId: risk.rowId,
            unitId: risk.unitId,
            riskCategoryId: risk.riskCategoryId,
            riskType: isNew ? props.riskType : risk.riskType,
            dateStr: Base.utcTimeToDateStr(risk.date),
            code: risk.code,
            title: risk.title,
            description: risk.description,
            threat: risk.threat,
            controls: risk.controls,
            owner: risk.owner,
            assesmentFrequency: risk.assesmentFrequency,
            opportunity: risk.opportunity,
            opportunityBenefit: risk.opportunityBenefit,
            opportunityDescription: risk.opportunityDescription,
            canEdit,
            canRemove,
            units: result.units,
            riskCategories: [IdTitleNode.createIdTitleNode(Base.emptyGuid, `'${Translations.Select}'`, [])].concat(result.riskCategories),
            riskProcesses: result.riskProcesses,
            riskStandards: result.riskStandards,
            riskStakeholders: result.riskStakeholders,
            riskRiskProcessIds: result.riskRiskProcessIds,
            riskRiskStandardIds: result.riskRiskStandardIds,
            riskRiskStakeholderIds: result.riskRiskStakeholderIds,
            riskProcessName: result.riskProcessName,
            lockedUser: risk.lockedUser,
            isLockedToOtherUser: risk.isLockedToOtherUser(props.sessionState.code),
            activeTab: isNew
                ? RiskEditDialogTab.General
                : state.activeTab,
        };
        setState((old) => ({
            ...old,
            ...newState
        }));
        orgStateHash.current = getSaveDataFromState(newState).hash;
        //Add locked record to idb
        if (!isNew && state.canEdit && LockedRecord.isLockedToUser(risk.lockedUser, props.sessionState.code)) {
            recordLockManager.addLock(LockedRecord.createLockedRecord(props.companyId, risk.id, props.sessionState.code, DatabaseRecordType.Risk));
        }
        //Send read receipt for risk
        if (!isNew && !risk.isRead) {
            const userIsCreator = risk.createUser.code === props.sessionState.code;
            if (!userIsCreator) {
                AppUtils.callService(() => riskService.sendRiskReadReceipt(props.companyId, risk.id), false, true);
            }
        }
    };

    const componentWillUnmount = useRef(false);

    useEffect(() => {
        _isMounted.current = true;
        
        return () => {
            _isMounted.current = false;
            componentWillUnmount.current = true;
        };
    }, []);

    useEffect(() => {
        const loadData = async () => {
            if (state.id) {
                const cancelResult = await AppUtils.cancel(getSaveDataFromState(state).hash, orgStateHash.current, null);
                if (cancelResult === ConfirmationDialogResult.Cancel) return;
                if (cancelResult === ConfirmationDialogResult.Yes) {
                    if (!await saveToDb()) return;
                    await props.onRefreshList();
                }
            }
            await loadRisk(props.riskId);
        };

        loadData();
    }, [props.riskId]);

    useEffect(() => {
        const unlockRecord = () => {
            if (!state.isNew && state.canEdit && LockedRecord.isLockedToUser(state.lockedUser, props.sessionState.code)) {
                recordLockManager.unlock(props.companyId, state.id, DatabaseRecordType.Risk);
            }
        };

        return () => {
            if (componentWillUnmount.current) {
                unlockRecord();
            }
        };
    }, [state.id, state.isNew, state.canEdit, state.lockedUser]);

    const handleToggleActiveTab = async (activeTab: RiskEditDialogTab) => {
        if (state.activeTab === activeTab) return;
        if (state.activeTab === RiskEditDialogTab.General) {
            if (!await validateBeforeSave()) return;
        }
        setState(old => ({ ...old, activeTab }));
    };

    // #region Locking
    const handleUnlockAndLockToMe = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(state.riskType === RiskType.Risk
            ? Translations.DoYouReallyWantToUnlockRisk
            : Translations.DoYouReallyWantToUnlockOpportunity, state.risk.code), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const result = await recordLockManager.lock(props.companyId, state.id, props.sessionState.code, DatabaseRecordType.Risk);
        if (!result || result.items.length < 1) return;
        setState((old) => ({
            ...old,
            lockedUser: StoreUtils.getNowModifyUser(result.items[0].lockedDate),
            isLockedToOtherUser: false
        }));
    };
    // #endregion Locking

    const afterSaveToDb = (id: string) => {
        loadRisk(id);
        props.onSaved(id);
    };

    const handleSave = async (closeDialog: boolean) => {
        const result = await saveToDb();
        if (!result) return;
        if (!closeDialog) {
            afterSaveToDb(result.id);
            return;
        }
        props.onClose();
    };

    const handleRemove = async () => {
        if (state.isLockedToOtherUser || state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(state.riskType === RiskType.Risk
            ? Translations.DoYouReallyWantToRemoveRiskParameterPermanently
            : Translations.DoYouReallyWantToRemoveOpportunityParameterPermanently, state.risk.code), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => riskService.removeRisk(props.companyId, state.risk.id));
            if (!result) return;
            setState(old => ({ ...old, lockedUser: null }));
            props.onClose();
        }
    };

    // #region Saving
    const getSaveDataFromState = (state: IRiskEditDialogState): SaveDataObj => {
        const isRisk = state.riskType === RiskType.Risk;
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("unitId", state.unitId);
        result.append("riskCategoryId", state.riskCategoryId);
        result.append("riskType", state.riskType);
        result.append("date", state.dateStr.toUtcDate().getTime());
        result.append("title", state.title);
        result.append("description", isRisk ? state.description : null);
        result.append("threat", isRisk ? state.threat : null);
        result.append("controls", isRisk ? state.controls : null);
        result.append("owner", state.owner);
        result.append("assesmentFrequency", isRisk ? state.assesmentFrequency : RiskAssesmentFrequency.Continuous);
        result.append("opportunity", !isRisk || state.opportunity);
        result.append("opportunityBenefit", state.opportunityBenefit);
        result.append("opportunityDescription", state.opportunityDescription);
        result.append("processIds", state.riskRiskProcessIds);
        result.append("standardIds", state.riskRiskStandardIds);
        result.append("stakeholderIds", state.riskRiskStakeholderIds);
        return result;
    };

    const checkErrors = (): Promise<boolean> => {
        const isRisk = state.riskType === RiskType.Risk;
        const opportunity = !isRisk || state.opportunity;
        return new Promise<boolean>((resolve) => {
            if (!state.riskCategoryId || state.riskCategoryId === Base.emptyGuid) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.RiskCategoryMustBeDefined));
                return resolve(false);
            }
            if (!state.owner) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.RiskOwnerMustBeDefined));
                return resolve(false);
            }
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            if (!state.dateStr) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.DateMustBeDefined));
                return resolve(false);
            }
            if (!state.title) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.TitleMustBeDefined));
                return resolve(false);
            }
            if (isRisk) {
                if (!state.description) {
                    store.customStore.dispatch(storeActions.showErrorMessage(Translations.DescriptionMustBeDefined));
                    return resolve(false);
                }
                if (!state.threat) {
                    store.customStore.dispatch(storeActions.showErrorMessage(Translations.RiskThreatMustBeDefined));
                    return resolve(false);
                }
                if (!state.controls) {
                    store.customStore.dispatch(storeActions.showErrorMessage(Translations.RiskControlsMustBeDefined));
                    return resolve(false);
                }
            }
            if (opportunity) {
                if (!state.opportunityBenefit) {
                    store.customStore.dispatch(storeActions.showErrorMessage(Translations.OpportunityBenefitMustBeDefined));
                    return resolve(false);
                }
                if (!state.opportunityDescription) {
                    store.customStore.dispatch(storeActions.showErrorMessage(Translations.OpportunityDescriptionMustBeDefined));
                    return resolve(false);
                }
            }
            return resolve(true);
        });
    };

    const validateBeforeSave = async (): Promise<boolean> => {
        return await AppUtils.validate(checkErrors);
    };

    const saveToDb = async (): Promise<SaveSuccess> => {
        if (!await validateBeforeSave()) return null;
        const saveData = getSaveDataFromState(state);
        //Call server
        const result = await AppUtils.callService(() => riskService.saveRiskEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        setState(old => {
            const newState = { ...old, id: result.id, rowId: result.rowId, isNew: false };
            orgStateHash.current = getSaveDataFromState(newState).hash;
            return newState;
        });
        return result;
    };

    const saveToDbIfChanged = async (): Promise<ISaveSuccess> => {
        if (getSaveDataFromState(state).hash === orgStateHash.current) {
            return { message: "", errorMessage: "", id: state.id, rowId: state.rowId };
        }
        return await saveToDb();
    };
    // #endregion Saving

    // #region Publish
    const getPublishData = (saveSuccess: SaveSuccess): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", saveSuccess.id);
        result.append("rowId", saveSuccess.rowId);
        result.append("companyId", props.companyId);
        return result;
    };

    const handlePublish = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(state.riskType === RiskType.Risk
            ? Translations.DoYouReallyWantToPublishRiskParameter
            : Translations.DoYouReallyWantToPublishOpportunityParameter, state.code), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const saveResult = await saveToDbIfChanged();
        if (!saveResult) return;
        const result = await AppUtils.callService(() => riskService.publishRisk(getPublishData(saveResult).obj));
        if (!result) return;
        afterSaveToDb(result.id);
    };

    const handleUnPublish = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(state.riskType === RiskType.Risk
            ? Translations.DoYouReallyWantToUnpublishRiskParameter
            : Translations.DoYouReallyWantToUnpublishOpportunityParameter, state.code), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const saveResult = await saveToDbIfChanged();
        if (!saveResult) return;
        const result = await AppUtils.callService(() => riskService.unpublishRisk(props.companyId, saveResult.id, saveResult.rowId));
        if (!result) return;
        afterSaveToDb(result.id);
    };
    // #endregion Publish

    // #region Archive
    const handleArchive = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(state.riskType === RiskType.Risk
            ? Translations.DoYouReallyWantToArchiveRiskParameter
            : Translations.DoYouReallyWantToArchiveOpportunityParameter, state.risk.code), false, archiveConfirmationWord) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const saveResult = await saveToDbIfChanged();
        if (!saveResult) return;
        const result = await AppUtils.callService(() => riskService.archiveRisk(props.companyId, saveResult.id, saveResult.rowId));
        if (!result) return;
        afterSaveToDb(result.id);
    };

    const handleRestoreFromArchive = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(state.riskType === RiskType.Risk
            ? Translations.DoYouReallyWantToRestoreRiskParameterFromArchive
            : Translations.DoYouReallyWantToRestoreOpportunityParameterFromArchive, state.risk.code), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const saveResult = await saveToDbIfChanged();
        if (!saveResult) return;
        const result = await AppUtils.callService(() => riskService.restoreRiskFromArchive(props.companyId, saveResult.id, saveResult.rowId));
        if (!result) return;
        afterSaveToDb(result.id);
    };
    // #endregion Archive

    // #region Trash Bin
    const handleMoveToTrashBin = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(state.riskType === RiskType.Risk
            ? Translations.DoYouReallyWantToMoveRiskParameterToTrashBin
            : Translations.DoYouReallyWantToMoveOpportunityParameterToTrashBin, state.risk.code), false, removeConfirmationWord) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const saveResult = await saveToDbIfChanged();
        if (!saveResult) return;
        const result = await AppUtils.callService(() => riskService.moveRiskToTrashBin(props.companyId, saveResult.id, saveResult.rowId));
        if (!result) return;
        afterSaveToDb(result.id);
    };

    // const handleRestoreFromTrashBin = async () => {
    //     if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRestoreRiskParameterFromATrashBin, state.risk.code), false) !== ConfirmationDialogResult.Yes) {
    //         return;
    //     }
    //     const saveResult = await saveToDbIfChanged();
    //     if (!saveResult) return;
    //     const result = await AppUtils.callService(() => riskService.restoreRiskFromTrashBin(props.companyId, saveResult.id, saveResult.rowId));
    //     if (!result) return;
    //     afterSaveToDb(result.id);
    // };
    // #endregion Trash Bin

    const handleOpportunityChange = async (opportunity: boolean) => {
        if (!opportunity && (state.opportunityBenefit || state.opportunityDescription)) {
            const confirmationResult = await AppUtils.showConfirmationDialog(Translations.DoYouWantToEraseOpportunityBenefitAndDescription) === ConfirmationDialogResult.Yes;
            if (confirmationResult) {
                setState((old) => ({ ...old, opportunity, opportunityBenefit: "", opportunityDescription: "" }));
                return;
            }
        }
        setState((old) => ({ ...old, opportunity }));
    };

    // const handlePrint = async () => {
    //     if (state.isNew) return;
    //     AppUtils.showErrorMessage(Translations.NotImplementedYet);
    //     // props.onPrint();
    // };

    const handleCancel = async () => {
        const cancelResult = await AppUtils.cancel(getSaveDataFromState(state).hash, orgStateHash.current, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            handleSave(true);
        } else {
            props.onClose();
        }
    };

    const canMoveOut = async (): Promise<boolean> => {
        return await AppUtils.canMoveOut(getSaveDataFromState(state).hash, orgStateHash.current,
            async () => !!await saveToDb(),
            props.onRefreshList,
            (newStateHash) => { orgStateHash.current = newStateHash; });
    };

    const handlePrevious = async () => {
        if (!await canMoveOut()) return;
        props.onPrevious();
    };

    const handleNext = async () => {
        if (!await canMoveOut()) return;
        props.onNext();
    };

    if (!state.risk) return null;

    const isRisk = state.riskType === RiskType.Risk;

    const isPublished = !!state.risk.publishUser;
    const isArchived = !!state.risk.archiveUser;
    const isInTrash = !!state.risk.deleteUser;
    const isReadOnly = !(state.canEdit && !isArchived);
        
    const canPublish = !state.isNew && !isArchived && !isInTrash && !state.isLockedToOtherUser && state.canEdit;
    const canUnPublish = !state.isNew && !isArchived && !isInTrash && isPublished && !state.isLockedToOtherUser && state.canEdit;
    const modifiedAfterPublish = isPublished && state.risk.publishUser.date < state.risk.modifyUser.date;

    const canArchive = !state.isNew && !state.isLockedToOtherUser && !isInTrash && !isArchived && state.canEdit;
    const canRestoreFromArchive = !state.isNew && !state.isLockedToOtherUser && !isInTrash && isArchived && state.canEdit;

    const canMoveToTrash = !state.isNew && !state.isLockedToOtherUser && !isInTrash && state.canRemove;
    //const canRestoreFromTrash = !state.isNew && !state.isLockedToOtherUser && isInTrash && state.canRemove;

    const canDeletePermanently = !state.isNew && !state.isLockedToOtherUser && isInTrash && state.canRemove;

    const headerFields = <>
        <GridRow>
            <Grid item sm={3}>
                <FormField title={Translations.Code} fullWidth margin="0">
                    <FormText
                        required={true}
                        disabled={true}
                        tooltip={Translations.Code}
                        value={state.code}
                        onChange={null}
                    />
                </FormField>
            </Grid>
            <Grid item sm={3}>
                <FormField title={Translations.Unit} margin="0">
                    <TreeCheckboxDropdown
                        required={true}
                        disabled={isReadOnly}
                        singleSelect={true}
                        selectedIds={[state.unitId]}
                        showCommentInDropdown={true}
                        values={state.units}
                        onChange={(unitIds: string[]) => setState((old) => ({ ...old, unitId: unitIds[0] }))}
                    />
                </FormField>
            </Grid>
            <Grid item sm={3}>
                <FormField title={Translations.Date} fullWidth required margin="0">
                    <DatePicker
                        disabled={isReadOnly}
                        value={state.dateStr}
                        onChange={(dateStr: string) => setState((old) => ({ ...old, dateStr }))}
                    />
                </FormField>
            </Grid>
            <Grid item sm={3}>
                <FormField title={Translations.State} fullWidth margin="0">
                    <RiskListStateChip
                        code={isInTrash
                            ? Translations.InTrashBin
                            : (isArchived
                                ? Translations.Archived
                                : (isPublished
                                    ? Translations.Published
                                    : Translations.Draft))}
                        color={isInTrash
                            ? theme.palette.stateLightGray.main //InTrashBin
                            : (isArchived
                                ? theme.palette.stateGray.main //Archived
                                : (isPublished
                                    ? theme.palette.stateGreen.main //Published
                                    : theme.palette.stateGray.main //Draft
                                )
                            )
                        }
                    />
                </FormField>
            </Grid>
        </GridRow>
        <GridRow>
            <Grid item sm={3}>
                <FormField title={Translations.AType}>
                    <IdTitleDropdown
                        disabled={isReadOnly || !state.isNew}
                        selectedId={state.riskType.toString()}
                        items={[
                            { id: RiskType.Risk.toString(), title: Translations.Risk },
                            { id: RiskType.Opportunity.toString(), title: Translations.Opportunity }
                        ]}
                        onChange={(riskType: string) => setState((old) => ({ ...old, riskType: riskType.toInteger() }))}
                    />
                </FormField>
            </Grid>
            <Grid item sm={3}>
                <FormField title={Translations.RiskCategory} required>
                    <TreeCheckboxDropdown
                        disabled={isReadOnly}
                        selectedIds={[state.riskCategoryId]}
                        singleSelect
                        values={state.riskCategories}
                        onChange={(riskCategoryIds: string[]) => setState((old) => ({ ...old, riskCategoryId: riskCategoryIds.length ? riskCategoryIds[0] : old.riskCategoryId }))}
                    />
                </FormField>
            </Grid>
            <Grid item sm={3}>
                <FormField title={Translations.RiskOwner} fullWidth required>
                    <FormText
                        disabled={isReadOnly}
                        maxLength={100}
                        required={true}
                        tooltip={Translations.RiskOwner}
                        value={state.owner}
                        onChange={(owner) => setState((old) => ({ ...old, owner }))}
                    />
                </FormField>
            </Grid>
            {isRisk &&
                <Grid item sm={3}>
                    <FormField title={Translations.RiskAssesmentFrequency}>
                        <IdTitleDropdown
                            disabled={isReadOnly}
                            selectedId={state.assesmentFrequency.toString()}
                            items={[
                                { id: RiskAssesmentFrequency.Continuous.toString(), title: Translations.Continuous },
                                { id: RiskAssesmentFrequency.Monthly.toString(), title: Translations.GroupByMonth },
                                { id: RiskAssesmentFrequency.Quarterly.toString(), title: Translations.GroupByQuarter },
                                { id: RiskAssesmentFrequency.HalfYearly.toString(), title: Translations.GroupByHalfYear },
                                { id: RiskAssesmentFrequency.Yearly.toString(), title: Translations.GroupByYear }
                            ]}
                            onChange={(assesmentFrequency: string) => setState((old) => ({ ...old, assesmentFrequency: assesmentFrequency.toInteger() }))}
                        />
                    </FormField>
                </Grid>
            }
        </GridRow>
        <GridRow>
            <Grid item sm={12}>
                <FormField title={Translations.Title} fullWidth required>
                    <FormText
                        disabled={isReadOnly}
                        maxLength={100}
                        tooltip={Translations.Title}
                        value={state.title}
                        onChange={(title) => setState((old) => ({ ...old, title }))}
                    />
                </FormField>
            </Grid>
        </GridRow>
    </>;

    const riskFields = isRisk
        ? <>
            <GridRow>
                <Grid item sm={12}>
                    <FormField title={Translations.Description} fullWidth required>
                        <FormText
                            disabled={isReadOnly}
                            maxLength={1000}
                            placeholder={Translations.RiskDescriptionPlaceholder}
                            tooltip={Translations.Description}
                            value={state.description}
                            multiline
                            rows={5}
                            onChange={(description) => setState((old) => ({ ...old, description }))}
                        />
                    </FormField>
                </Grid>
            </GridRow>
            <GridRow>
                <Grid item sm={6}>
                    <FormField title={Translations.RiskThreat} fullWidth required>
                        <FormText
                            disabled={isReadOnly}
                            maxLength={1000}
                            placeholder={Translations.RiskThreatPlaceholder}
                            tooltip={Translations.RiskThreat}
                            value={state.threat}
                            multiline
                            rows={6}
                            onChange={(threat) => setState((old) => ({ ...old, threat }))}
                        />
                    </FormField>
                </Grid>
                <Grid item sm={6}>
                    <FormField title={Translations.RiskControls} fullWidth required>
                        <FormText
                            disabled={isReadOnly}
                            maxLength={1000}
                            placeholder={Translations.RiskControlsPlaceholder}
                            tooltip={Translations.RiskControls}
                            value={state.controls}
                            multiline
                            rows={6}
                            onChange={(controls) => setState((old) => ({ ...old, controls }))}
                        />
                    </FormField>
                </Grid>
            </GridRow>
        </>
        : null;

    const opportunityFields = !isRisk || state.opportunity
        ? [
            <Box key="A" sx={!isRisk ? { paddingBottom: "1.25rem", height: "100%" } : undefined}>
                <FormField title={Translations.OpportunityBenefit} fullWidth required fitToContainerHeight={!isRisk}>
                    <FormText
                        disabled={isReadOnly}
                        fitToContainerHeight={!isRisk}
                        maxLength={1000}
                        placeholder={Translations.OpportunityBenefitPlaceholder}
                        tooltip={Translations.OpportunityBenefit}
                        value={state.opportunityBenefit}
                        multiline
                        rows={6}
                        onChange={(opportunityBenefit) => setState((old) => ({ ...old, opportunityBenefit }))}
                    />
                </FormField>
            </Box>,
            <Box key="B" sx={!isRisk ? { height: "100%" } : undefined}>
                <FormField title={Translations.OpportunityDescription} fullWidth required fitToContainerHeight={!isRisk}>
                    <FormText
                        disabled={isReadOnly}
                        fitToContainerHeight={!isRisk}
                        maxLength={1000}
                        placeholder={Translations.OpportunityDescriptionPlaceholder}
                        tooltip={Translations.OpportunityDescription}
                        value={state.opportunityDescription}
                        multiline
                        rows={6}
                        onChange={(opportunityDescription) => setState((old) => ({ ...old, opportunityDescription }))}
                    />
                </FormField>
            </Box>
        ]
        : null;

    return (
        <Dialog
            id="riskEditDialog"
            maxWidth="xl"
            maxHeight="100%"
            title={state.isNew
                ? (isRisk ? Translations.AddRisk : Translations.AddOpportunity)
                : `${(isRisk ? Translations.EditRisk : Translations.EditOpportunity)}: ${state.risk.code}${(isArchived ? ", " + Translations.Archived : "")}${(isReadOnly ? ", " + Translations.ReadOnly : "")}${(state.isLockedToOtherUser ? ", " + Translations.Locked : "")}`
            }
            dialogTabs={
                <DialogTabs
                    activeTabId={state.activeTab}
                    tabs={[
                        { id: RiskEditDialogTab.General, title: Translations.BasicInformation },
                        { id: RiskEditDialogTab.Assessments, title: Translations.Assessments, hidden: !isRisk },
                        { id: RiskEditDialogTab.Attachments, title: Translations.Attachments },
                    ]}
                    onChangeTab={handleToggleActiveTab}
                />
            }
            body={
                <>
                    {state.activeTab === RiskEditDialogTab.General &&
                        <Box height="100%">
                            <GridRow height="100%">
                                <Grid item sm={8} height={isRisk ? "calc(100% + 30px)" : "100%"}>
                                    <Box height="100%" sx={isRisk ? { overflowY: "auto", position: "relative" } : undefined}>
                                        {(isRisk
                                            ? <>
                                                {headerFields}
                                                {riskFields}
                                                <DialogSectionCard headerTitle={Translations.Opportunity} height="auto" margin="30px 0 0 0" className="opportunitySection">
                                                    <GridRow>
                                                        <Grid item sm={12}>
                                                            <FormCheckBox
                                                                title={Translations.RiskContainsOpportunity}
                                                                checked={state.opportunity}
                                                                disabled={isReadOnly}
                                                                onChange={handleOpportunityChange}
                                                            />
                                                        </Grid>
                                                    </GridRow>
                                                    {opportunityFields}
                                                </DialogSectionCard>
                                            </>
                                            : <VerticalContainer
                                                before={headerFields}
                                            >
                                                <VerticalContainer minHeight="250px">
                                                    {opportunityFields}
                                                </VerticalContainer>
                                            </VerticalContainer>)}
                                    </Box>
                                </Grid>
                                <Grid item sm={4} height="calc(100% + 30px)"sx={{ overflowY: "auto" }}>
                                    {state.riskStandards.length > 0 &&
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <FormField title={Translations.RiskStandard} margin={0}>
                                                    <TreeCheckboxDropdown
                                                        disabled={isReadOnly}
                                                        selectedIds={state.riskRiskStandardIds}
                                                        values={state.riskStandards}
                                                        onChange={(riskRiskStandardIds: string[]) => setState((old) => ({ ...old, riskRiskStandardIds }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                    }
                                    {state.riskStakeholders.length > 0 &&
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <FormField title={Translations.RiskStakeholder} margin={state.riskStandards.length > 0 ? undefined : 0}>
                                                    <TreeCheckboxDropdown
                                                        disabled={isReadOnly}
                                                        selectedIds={state.riskRiskStakeholderIds}
                                                        values={state.riskStakeholders}
                                                        onChange={(riskRiskStakeholderIds: string[]) => setState((old) => ({ ...old, riskRiskStakeholderIds }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                    }
                                    {state.riskProcesses.length > 0 &&
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <FormField title={state.riskProcessName} margin={state.riskStandards.length > 0 || state.riskStakeholders.length > 0 ? undefined : 0}>
                                                    <TreeCheckboxDropdown
                                                        disabled={isReadOnly}
                                                        selectedIds={state.riskRiskProcessIds}
                                                        values={state.riskProcesses}
                                                        onChange={(riskRiskProcessIds: string[]) => setState((old) => ({ ...old, riskRiskProcessIds }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                    }
                                    {!state.isNew &&
                                        <>
                                            <GridRow marginTop={state.riskStandards.length > 0 || state.riskStakeholders.length > 0 || state.riskProcesses.length > 0 ? "calc(1.5rem - 16px)" : undefined}>
                                                <Grid item sm={12}>
                                                    <FormField title={Translations.TaskCreated} fullWidth margin={0}>
                                                        <ModifyUserInfo
                                                            modifyUser={state.risk.createUser}
                                                        />
                                                    </FormField>
                                                </Grid>
                                            </GridRow>
                                            <GridRow>
                                                <Grid item sm={12}>
                                                    <FormField title={Translations.Modified} fullWidth>
                                                        <ModifyUserInfo
                                                            modifyUser={state.risk.modifyUser}
                                                        />
                                                    </FormField>
                                                </Grid>
                                            </GridRow>
                                            <GridRow>
                                                <Grid item sm={12}>
                                                    <FormField title={Translations.Published} fullWidth>
                                                        <ModifyUserInfo
                                                            modifyUser={state.risk.publishUser}
                                                            suffixElement={canPublish || canUnPublish
                                                                ? <>
                                                                    {canPublish &&
                                                                        <PublishButton
                                                                            published={isPublished}
                                                                            modifiedAfterPublish={modifiedAfterPublish}
                                                                            onClick={handlePublish}
                                                                        />
                                                                    }
                                                                    {canUnPublish &&
                                                                        <RemoveToolButton
                                                                            tooltip={Translations.Unpublish}
                                                                            onClick={handleUnPublish}
                                                                        />
                                                                    }
                                                                </>
                                                                : null
                                                            }
                                                        />
                                                        {isPublished && modifiedAfterPublish &&
                                                            <Alert severity="info" sx={{ marginTop: ".5rem" }}>
                                                                {isRisk ? Translations.RiskIsModifiedAfterItWasPublished : Translations.OpportunityIsModifiedAfterItWasPublished}
                                                            </Alert>
                                                        }
                                                    </FormField>
                                                </Grid>
                                            </GridRow>
                                        </>
                                    }
                                    {!!state.risk.archiveUser &&
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <FormField title={Translations.Archived} fullWidth>
                                                    <ModifyUserInfo
                                                        modifyUser={state.risk.archiveUser}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                    }
                                    {!!state.risk.deleteUser &&
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <FormField title={Translations.Removed} fullWidth>
                                                    <ModifyUserInfo
                                                        modifyUser={state.risk.deleteUser}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                    }
                                </Grid>
                            </GridRow>
                        </Box>
                    }
                    {state.activeTab === RiskEditDialogTab.Assessments &&
                        <RiskEditDialogAssessmentList
                            sessionState={props.sessionState}
                            companyId={props.companyId}
                            riskId={state.id}
                            unitId={state.unitId}
                            assesmentFrequency={state.assesmentFrequency}
                            code={state.code}
                            title={state.title}
                            description={state.description}
                            threat={state.threat}
                            controls={state.controls}
                            isReadOnly={isReadOnly}
                            isNew={state.isNew}
                        />
                    }
                    {state.activeTab === RiskEditDialogTab.Attachments &&
                        <AttachmentListOfRecord
                            companyId={props.companyId}
                            recordId={state.id}
                            title={null}
                            margin="0"
                            onGetAttachments={riskService.getRiskAttachments}
                            onRemoveAttachments={riskService.riskRemoveAttachments}
                            onAddAttachments={riskService.riskAddAttachments}
                            isReadOnly={isReadOnly}
                            isNewMessage={state.isNew
                                ? Translations.NewRiskMustBeSavedBeforeAttachmentsCanBeAdded
                                : null
                            }
                        />
                    }
                </>
            }
            actions={
                <Box display="flex" justifyContent="space-between" width="100%">
                    <DialogItemsNav
                        hasPrevious={props.hasPrevious}
                        hasNext={props.hasNext}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                        createUser={!state.isNew ? state.risk.createUser : null}
                        modifyUser={!state.isNew ? state.risk.modifyUser : null}
                        archiveUser={!state.isNew ? state.risk.archiveUser : null}
                        publishUser={!state.isNew ? state.risk.publishUser : null}
                        deleteUser={!state.isNew ? state.risk.deleteUser : null}
                        lockedUser={state.canEdit ? state.lockedUser : null}
                        isLockedToOtherUser={!state.isNew && state.canEdit && state.isLockedToOtherUser}
                        onUnlockAndLockToMe={props.sessionState.isSuperAdmin ? handleUnlockAndLockToMe : null}
                    />
                    <DialogButtons
                        isNew={state.isNew}
                        removeDisabled={state.isLockedToOtherUser || !state.canRemove}
                        saveDisabled={state.isLockedToOtherUser}
                        removeTitle={state.risk.deleteUser ? (isRisk ? Translations.RemoveRiskPermanently : Translations.RemoveOpportunityPermanently) : null}
                        onCancel={handleCancel}
                        //onPrint={!state.isNew ? handlePrint : null}
                        onRemove={canDeletePermanently
                            ? handleRemove
                            : (canMoveToTrash
                                ? handleMoveToTrashBin
                                : null)}
                        onArchive={canArchive ? handleArchive : null}
                        onRestoreFromArchive={canRestoreFromArchive
                            ? handleRestoreFromArchive
                            : null
                        }
                        onSave={!isReadOnly ? () => handleSave(false) : null}
                    />
                </Box>
            }
            onClose={handleCancel}/>
    );
};